// Intent: "Skarbiec rodziny" (Stitch mock skarbiec_rodziny) — one safe place
// for the family's basics: members with PESEL + birth date, anniversaries and
// document scans. LOCAL-ONLY by design (no cloud sync; PESEL in the keychain,
// scans in the app sandbox). Web build hides PESEL + documents — SecureStore
// and the file sandbox don't exist there, and we refuse to fake safety.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Modal, Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { DateField } from '@/components/ui/date-field';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useHouseholdStore } from '@/store/household-store';
import {
  useVaultStore,
  type VaultDateKind,
  type VaultDocument,
  type VaultRelation,
} from '@/store/vault-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatDate } from '@/utils/format';

const IS_WEB = Platform.OS === 'web';

const RELATIONS: { key: VaultRelation; labelKey: string }[] = [
  { key: 'SELF', labelKey: 'vault.relSelf' },
  { key: 'PARTNER', labelKey: 'vault.relPartner' },
  { key: 'CHILD', labelKey: 'vault.relChild' },
  { key: 'OTHER', labelKey: 'vault.relOther' },
];

// The three standard document tiles from the mock; "new document" adds custom.
const STANDARD_DOCS = ['vault.docId', 'vault.docPassport', 'vault.docMarriage'] as const;
const DOC_ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  'vault.docId': 'card-outline',
  'vault.docPassport': 'globe-outline',
  'vault.docMarriage': 'document-text-outline',
};

/** "1985-05-12" -> "12.05.1985"; date tiles use short "15.06". */
function shortDayMonth(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
}

function maskPesel(): string {
  return '••••••••••• ';
}

export default function VaultScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const vault = useVaultStore();
  const household = useHouseholdStore((s) => s.household);

  // --- add-member form state ---
  const [addingMember, setAddingMember] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [birthDate, setBirthDate] = useState<string | null>(null);
  const [relation, setRelation] = useState<VaultRelation>('SELF');
  const [pesel, setPesel] = useState('');

  // --- member row expansion / reveal ---
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  // --- date editing ---
  const [editingDate, setEditingDate] = useState<VaultDateKind | null>(null);
  const [dateDraft, setDateDraft] = useState<string | null>(null);

  // --- documents ---
  const [viewDoc, setViewDoc] = useState<VaultDocument | null>(null);
  const [confirmDocDelete, setConfirmDocDelete] = useState(false);
  const [addingCustomDoc, setAddingCustomDoc] = useState(false);
  const [customDocLabel, setCustomDocLabel] = useState('');

  const wedding = vault.dates.find((d) => d.kind === 'WEDDING');
  const met = vault.dates.find((d) => d.kind === 'MET');

  const resetMemberForm = () => {
    setAddingMember(false);
    setFirstName('');
    setBirthDate(null);
    setRelation('SELF');
    setPesel('');
  };

  const saveMember = async () => {
    await vault.addMember({
      firstName,
      relation,
      birthDate,
      pesel: IS_WEB ? null : pesel || null,
    });
    resetMemberForm();
  };

  const reveal = async (memberId: string) => {
    if (revealed[memberId]) {
      setRevealed((r) => {
        const { [memberId]: _drop, ...rest } = r;
        return rest;
      });
      return;
    }
    const value = await vault.getPesel(memberId);
    if (value) setRevealed((r) => ({ ...r, [memberId]: value }));
  };

  const pickScan = async (label: string, fromCamera: boolean) => {
    if (fromCamera) {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) return;
    }
    const picked = fromCamera
      ? await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.9 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.9 });
    if (picked.canceled || !picked.assets?.length) return;
    await vault.addDocument(label, picked.assets[0].uri);
  };

  const saveDate = async () => {
    if (editingDate && dateDraft) await vault.setDate(editingDate, dateDraft);
    setEditingDate(null);
    setDateDraft(null);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('vault.title')} />

        {/* Security banner (honest per platform) */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            backgroundColor: colors.infoBg,
            borderRadius: radius.card,
            padding: spacing.md,
          }}
        >
          <Ionicons name="lock-closed-outline" size={24} color={colors.primary} />
          <View style={{ flex: 1, gap: 2 }}>
            <AppText variant="labelLg" color={colors.navy}>
              {IS_WEB ? t('vault.bannerWebTitle') : t('vault.bannerTitle')}
            </AppText>
            <AppText variant="bodyMd">{IS_WEB ? t('vault.bannerWebBody') : t('vault.bannerBody')}</AppText>
          </View>
        </View>

        {/* Members */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <AppText variant="headlineSm" style={{ flex: 1 }}>
            {t('vault.members')}
          </AppText>
          {!addingMember && (
            <Pressable
              accessibilityRole="button"
              onPress={() => setAddingMember(true)}
              style={{ minHeight: 44, justifyContent: 'center' }}
            >
              <AppText variant="labelLg" color={colors.primary}>
                + {t('vault.add')}
              </AppText>
            </Pressable>
          )}
        </View>

        {vault.members.map((member) => {
          const expanded = expandedId === member.id;
          const peselShown = revealed[member.id];
          return (
            <Card key={member.id} style={{ gap: spacing.sm }}>
              <Pressable
                accessibilityRole="button"
                onPress={() => {
                  setExpandedId(expanded ? null : member.id);
                  setConfirmRemoveId(null);
                }}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}
              >
                <View
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: radius.full,
                    backgroundColor: colors.primaryTile,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppText variant="labelLg" color={colors.navy}>
                    {member.firstName.charAt(0)}
                  </AppText>
                </View>
                <View style={{ flex: 1, gap: 2 }}>
                  <AppText variant="labelLg">{member.firstName}</AppText>
                  {member.hasPesel && (
                    <AppText variant="bodyMd">
                      PESEL {peselShown ?? maskPesel()}
                    </AppText>
                  )}
                  {member.birthDate && (
                    <AppText variant="bodyMd">
                      {t('vault.born', { date: formatDate(member.birthDate) })}
                    </AppText>
                  )}
                </View>
                <Ionicons name={expanded ? 'chevron-down' : 'chevron-forward'} size={20} color={colors.outlineVariant} />
              </Pressable>
              {expanded && (
                <View style={{ flexDirection: 'row', gap: spacing.md }}>
                  {member.hasPesel && !IS_WEB && (
                    <Pressable accessibilityRole="button" onPress={() => reveal(member.id)} style={{ minHeight: 40, justifyContent: 'center' }}>
                      <AppText variant="labelLg" color={colors.primary}>
                        {peselShown ? t('vault.hidePesel') : t('vault.showPesel')}
                      </AppText>
                    </Pressable>
                  )}
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => {
                      if (confirmRemoveId === member.id) {
                        void vault.removeMember(member.id);
                        setConfirmRemoveId(null);
                      } else {
                        setConfirmRemoveId(member.id);
                      }
                    }}
                    style={{ minHeight: 40, justifyContent: 'center' }}
                  >
                    <AppText variant="labelLg" color={colors.error}>
                      {confirmRemoveId === member.id ? t('vault.removeConfirm') : t('vault.remove')}
                    </AppText>
                  </Pressable>
                </View>
              )}
            </Card>
          );
        })}

        {/* Partial autofill nudge: declared kids but no CHILD in the vault yet */}
        {household?.declarations.hasKids && !vault.members.some((m) => m.relation === 'CHILD') && !addingMember && (
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              setRelation('CHILD');
              setAddingMember(true);
            }}
          >
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <Ionicons name="happy-outline" size={22} color={colors.primary} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {t('vault.kidsNudge')}
              </AppText>
            </Card>
          </Pressable>
        )}

        {/* Add-member form / empty-state tile */}
        {addingMember ? (
          <Card style={{ gap: spacing.md }}>
            <TextField label={t('vault.firstName')} value={firstName} onChangeText={setFirstName} />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
              {RELATIONS.map(({ key, labelKey }) => {
                const active = relation === key;
                return (
                  <Pressable
                    key={key}
                    accessibilityRole="button"
                    onPress={() => setRelation(key)}
                    style={{
                      backgroundColor: active ? colors.navy : colors.card,
                      borderRadius: radius.full,
                      borderWidth: 1,
                      borderColor: active ? colors.navy : colors.divider,
                      paddingHorizontal: spacing.md,
                      paddingVertical: spacing.xs,
                    }}
                  >
                    <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                      {t(labelKey)}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>
            <DateField label={t('vault.birthDate')} value={birthDate} onChange={setBirthDate} />
            {!IS_WEB && (
              <TextField
                label={t('vault.pesel')}
                keyboardType="numeric"
                value={pesel}
                onChangeText={(v) => setPesel(v.replace(/[^\d]/g, '').slice(0, 11))}
              />
            )}
            {IS_WEB && (
              <AppText variant="labelMd" color={colors.textSecondary}>
                {t('vault.peselWebHint')}
              </AppText>
            )}
            <PrimaryButton label={t('vault.saveMember')} disabled={!firstName.trim()} onPress={saveMember} />
            <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={resetMemberForm} />
          </Card>
        ) : (
          vault.members.length === 0 && (
            <Pressable
              accessibilityRole="button"
              onPress={() => setAddingMember(true)}
              style={{
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: colors.outlineVariant,
                borderRadius: radius.card,
                padding: spacing.lg,
                alignItems: 'center',
                gap: spacing.xs,
              }}
            >
              <Ionicons name="person-add-outline" size={26} color={colors.outline} />
              <AppText variant="labelLg" color={colors.outline}>
                {t('vault.addFirst')}
              </AppText>
            </Pressable>
          )
        )}

        {/* Important dates */}
        <AppText variant="headlineSm">{t('vault.dates')}</AppText>
        <View style={{ flexDirection: 'row', gap: spacing.sm }}>
          {(
            [
              { kind: 'WEDDING' as const, labelKey: 'vault.wedding', icon: 'heart-outline' as const, value: wedding },
              { kind: 'MET' as const, labelKey: 'vault.met', icon: 'sparkles-outline' as const, value: met },
            ]
          ).map(({ kind, labelKey, icon, value }) => (
            <Pressable
              key={kind}
              accessibilityRole="button"
              onPress={() => {
                setEditingDate(kind);
                setDateDraft(value?.date ?? null);
              }}
              style={{ flex: 1 }}
            >
              <Card style={{ gap: spacing.xs, alignItems: 'flex-start' }}>
                <Ionicons name={icon} size={22} color={kind === 'WEDDING' ? '#B4562A' : colors.primary} />
                <AppText variant="bodyMd">{t(labelKey)}</AppText>
                <AppText variant="headlineSm" color={colors.navy}>
                  {value ? shortDayMonth(value.date) : t('vault.setDate')}
                </AppText>
              </Card>
            </Pressable>
          ))}
        </View>

        {editingDate && (
          <Card style={{ gap: spacing.md }}>
            <DateField
              label={t(editingDate === 'WEDDING' ? 'vault.wedding' : 'vault.met')}
              value={dateDraft}
              onChange={setDateDraft}
            />
            <PrimaryButton label={t('vault.saveDate')} disabled={!dateDraft} onPress={saveDate} />
            <PrimaryButton
              label={t('common.cancel')}
              variant="secondary"
              onPress={() => {
                setEditingDate(null);
                setDateDraft(null);
              }}
            />
          </Card>
        )}

        {/* Reminders opt-in */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
          <AppText variant="bodyLg" style={{ flex: 1 }}>
            {t('vault.remind')}
          </AppText>
          <Switch
            value={vault.remindersEnabled}
            onValueChange={(v) => vault.setRemindersEnabled(v)}
            trackColor={{ false: colors.disabledBg, true: colors.primary }}
            thumbColor="#FFFFFF"
            disabled={IS_WEB}
          />
        </View>

        {/* Documents (native only — the web build has no private sandbox) */}
        {!IS_WEB && (
          <>
            <AppText variant="headlineSm">{t('vault.documents')}</AppText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm }}>
              {STANDARD_DOCS.map((labelKey) => {
                const label = t(labelKey);
                const doc = vault.documents.find((d) => d.label === label);
                return (
                  <Pressable
                    key={labelKey}
                    accessibilityRole="button"
                    onPress={() => {
                      if (doc) {
                        setViewDoc(doc);
                        setConfirmDocDelete(false);
                      } else {
                        void pickScan(label, false);
                      }
                    }}
                    style={{ width: '47%', flexGrow: 1 }}
                  >
                    <Card style={{ alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.lg }}>
                      <IconTile icon={DOC_ICONS[labelKey]} bg={colors.infoBg} fg={colors.primary} size={52} />
                      <AppText variant="labelLg" style={{ textAlign: 'center' }}>
                        {label}
                      </AppText>
                      <AppText variant="labelMd" color={colors.primary} style={{ letterSpacing: 0.8 }}>
                        {doc ? t('vault.viewScan') : t('vault.addScan')}
                      </AppText>
                    </Card>
                  </Pressable>
                );
              })}

              {/* Custom documents already added */}
              {vault.documents
                .filter((d) => !STANDARD_DOCS.some((k) => t(k) === d.label))
                .map((doc) => (
                  <Pressable
                    key={doc.id}
                    accessibilityRole="button"
                    onPress={() => {
                      setViewDoc(doc);
                      setConfirmDocDelete(false);
                    }}
                    style={{ width: '47%', flexGrow: 1 }}
                  >
                    <Card style={{ alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.lg }}>
                      <IconTile icon="document-attach-outline" bg={colors.infoBg} fg={colors.primary} size={52} />
                      <AppText variant="labelLg" style={{ textAlign: 'center' }}>
                        {doc.label}
                      </AppText>
                      <AppText variant="labelMd" color={colors.primary} style={{ letterSpacing: 0.8 }}>
                        {t('vault.viewScan')}
                      </AppText>
                    </Card>
                  </Pressable>
                ))}

              {/* New custom document tile */}
              <Pressable
                accessibilityRole="button"
                onPress={() => setAddingCustomDoc(true)}
                style={{
                  width: '47%',
                  flexGrow: 1,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: colors.outlineVariant,
                  borderRadius: radius.card,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: spacing.xs,
                  paddingVertical: spacing.lg,
                  minHeight: 140,
                }}
              >
                <Ionicons name="add-circle-outline" size={30} color={colors.outline} />
                <AppText variant="labelLg" color={colors.outline}>
                  {t('vault.newDoc')}
                </AppText>
              </Pressable>
            </View>

            {addingCustomDoc && (
              <Card style={{ gap: spacing.md }}>
                <TextField
                  label={t('vault.customDocLabel')}
                  placeholder={t('vault.customDocPlaceholder')}
                  value={customDocLabel}
                  onChangeText={setCustomDocLabel}
                />
                <PrimaryButton
                  label={t('vault.addScan')}
                  disabled={!customDocLabel.trim()}
                  onPress={async () => {
                    const label = customDocLabel.trim();
                    setAddingCustomDoc(false);
                    setCustomDocLabel('');
                    await pickScan(label, false);
                  }}
                />
                <PrimaryButton
                  label={t('common.cancel')}
                  variant="secondary"
                  onPress={() => {
                    setAddingCustomDoc(false);
                    setCustomDocLabel('');
                  }}
                />
              </Card>
            )}
          </>
        )}

        {/* Footer privacy pill */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.xs,
            backgroundColor: colors.card,
            borderRadius: radius.full,
            paddingVertical: spacing.sm,
            paddingHorizontal: spacing.md,
          }}
        >
          <Ionicons name="lock-closed" size={14} color={colors.textSecondary} />
          <AppText variant="labelMd">{IS_WEB ? t('vault.footerWeb') : t('vault.footer')}</AppText>
        </View>
      </ScrollView>

      {/* Scan viewer */}
      <Modal visible={viewDoc !== null} transparent animationType="fade" onRequestClose={() => setViewDoc(null)}>
        {/* Pad past the status bar (top) and the Android nav bar (bottom) so the
            close icon and the delete action are never hidden behind system UI. */}
        <View
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.85)',
            paddingHorizontal: spacing.screen,
            paddingTop: insets.top + spacing.md,
            paddingBottom: insets.bottom + spacing.lg,
            gap: spacing.md,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <AppText variant="headlineSm" color="#FFFFFF" style={{ flex: 1 }}>
              {viewDoc?.label}
            </AppText>
            <Pressable
              accessibilityRole="button"
              onPress={() => setViewDoc(null)}
              style={{ minWidth: 44, minHeight: 44, alignItems: 'flex-end', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={26} color="#FFFFFF" />
            </Pressable>
          </View>
          {viewDoc && (
            <Image source={{ uri: viewDoc.fileUri }} style={{ flex: 1, borderRadius: radius.card }} resizeMode="contain" />
          )}
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              if (!viewDoc) return;
              if (confirmDocDelete) {
                void useVaultStore.getState().removeDocument(viewDoc.id);
                setViewDoc(null);
              } else {
                setConfirmDocDelete(true);
              }
            }}
            style={{ minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <AppText variant="labelLg" color="#FF8A8A">
              {confirmDocDelete ? t('vault.removeConfirm') : t('vault.deleteScan')}
            </AppText>
          </Pressable>
        </View>
      </Modal>
    </SafeAreaView>
  );
}
